import { filterSuggestionItems } from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import { en } from "@blocknote/core/locales";
import {
  FormattingToolbar,
  FormattingToolbarController,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  getFormattingToolbarItems,
  useCreateBlockNote,
} from "@blocknote/react";
import { BlockNoteView } from "@blocknote/shadcn";
import "@blocknote/shadcn/style.css";
import {
  AIMenu,
  AIMenuController,
  AIToolbarButton,
  createAIExtension,
  getAIExtension,
  getAISlashMenuItems,
  getDefaultAIMenuItems,
} from "@blocknote/xl-ai";
import { en as aiEn } from "@blocknote/xl-ai/locales";
import "@blocknote/xl-ai/style.css";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TEST_IDS } from "../../../constants/testIds";
import { useCanvas } from "../../../contexts/CanvasContext";
import {
  addRelatedTopics,
  createAIModel,
  expandContent,
  improveWriting,
  makeInformal,
  summarize,
} from "../../../lib/ai/blocknoteAI";
import { Skeleton } from "../../ui/skeleton";
// import CollaborationStatus from "../CollaborationStatus";

// Create a default empty block for initialization
const createDefaultBlock = () => ({
  id: `paragraph_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  type: "paragraph",
  props: {
    textColor: "default",
    backgroundColor: "default",
    textAlignment: "left",
  },
  content: [],
  children: [],
});

// Clean up content that may be wrapped in markdown code blocks
const cleanMarkdownContent = (content) => {
  if (!content || typeof content !== "string") return content;

  // Remove markdown code block wrapper if present
  const markdownBlockMatch = content.match(/^```markdown\s*\n([\s\S]*?)\n```$/);
  if (markdownBlockMatch) {
    return markdownBlockMatch[1].trim();
  }

  // Remove any other code block wrapper if present
  const codeBlockMatch = content.match(/^```[\w]*\s*\n([\s\S]*?)\n```$/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  return content;
};

// Create a custom history plugin that preserves initial state
const _createHistoryWithInitialState = (initialBlocks) => {
  let storedInitialBlocks = initialBlocks;

  return {
    name: "preserveInitialHistory",
    addCommands() {
      return {
        undo:
          () =>
          ({ commands, editor, state }) => {
            try {
              // Get current editor content as blocks
              const currentBlocks = editor.getDocument
                ? editor.getDocument()
                : [];

              // Update stored initial blocks if they've changed
              if (
                window.canvasBlockNoteEditor === editor &&
                window.canvasInitialBlocks
              ) {
                storedInitialBlocks = window.canvasInitialBlocks;
              }

              // Check if current content matches initial state
              const isAtInitialState =
                JSON.stringify(currentBlocks) ===
                JSON.stringify(storedInitialBlocks);

              if (isAtInitialState) {
                return false;
              }

              // Check if undo is actually available using safer approach
              let hasUndoHistory = false;
              try {
                // Try different ways to check for undo history
                if (state.history$ && state.history$.prevRanges) {
                  hasUndoHistory = state.history$.prevRanges.length > 0;
                } else if (state.history && state.history.done) {
                  hasUndoHistory = state.history.done.length > 0;
                } else {
                  // Fallback: assume undo is available if we're not at initial state
                  hasUndoHistory = true;
                }
              } catch (historyError) {
                console.warn(
                  "🎨 TextEditor: Could not check undo history:",
                  historyError
                );
                hasUndoHistory = true; // Default to allowing undo
              }

              if (!hasUndoHistory) {
                return false;
              }

              // Perform undo if we're not at initial state
              return commands.undo();
            } catch (error) {
              console.warn(
                "🎨 TextEditor: Error in custom undo command:",
                error
              );
              // Fallback to default undo
              try {
                return commands.undo();
              } catch (fallbackError) {
                console.warn(
                  "🎨 TextEditor: Fallback undo also failed:",
                  fallbackError
                );
                return false;
              }
            }
          },
        redo:
          () =>
          ({ commands }) => {
            try {
              return commands.redo();
            } catch (error) {
              console.warn("🎨 TextEditor: Error in redo command:", error);
              return false;
            }
          },
      };
    },
    addKeyboardShortcuts() {
      return {
        "Mod-z": () => {
          try {
            return this.editor.commands.undo();
          } catch (error) {
            console.warn("🎨 TextEditor: Error in undo shortcut:", error);
            return false;
          }
        },
        "Mod-y": () => {
          try {
            return this.editor.commands.redo();
          } catch (error) {
            console.warn("🎨 TextEditor: Error in redo shortcut:", error);
            return false;
          }
        },
        "Mod-Shift-z": () => {
          try {
            return this.editor.commands.redo();
          } catch (error) {
            console.warn("🎨 TextEditor: Error in redo shortcut:", error);
            return false;
          }
        },
      };
    },
    onCreate() {
      try {
        // Store initial blocks reference globally for access
        if (window && this.editor) {
          window.canvasInitialBlocks = storedInitialBlocks;
        }
      } catch (error) {
        console.warn("🎨 TextEditor: Error in onCreate:", error);
      }
    },
    onUpdate() {
      try {
        // Update the stored initial blocks if needed
        if (window && this.editor && window.canvasInitialBlocks) {
          storedInitialBlocks = window.canvasInitialBlocks;
        }
      } catch (error) {
        console.warn("🎨 TextEditor: Error in onUpdate:", error);
      }
    },
  };
};

// Custom AI Menu Component
function CustomAIMenu() {
  return (
    <AIMenu
      items={(editor, aiResponseStatus) => {
        if (aiResponseStatus === "user-input") {
          // Returns different items based on whether the AI Menu was
          // opened via the Formatting Toolbar or the Slash Menu.
          if (editor.getSelection()) {
            return [
              // Gets the default AI Menu items
              ...getDefaultAIMenuItems(editor, aiResponseStatus),
              // Adds our custom items for selected text
              makeInformal(editor),
              improveWriting(editor),
              summarize(editor),
            ];
          } else {
            return [
              // Gets the default AI Menu items
              ...getDefaultAIMenuItems(editor, aiResponseStatus),
              // Adds our custom items for new content
              addRelatedTopics(editor),
              expandContent(editor),
            ];
          }
        }
        // for other states, return the default items
        return getDefaultAIMenuItems(editor, aiResponseStatus);
      }}
    />
  );
}

// Formatting toolbar with the `AIToolbarButton` added
function FormattingToolbarWithAI() {
  return (
    <FormattingToolbarController
      formattingToolbar={() => (
        <FormattingToolbar>
          {getFormattingToolbarItems()}
          <AIToolbarButton />
        </FormattingToolbar>
      )}
    />
  );
}

// Slash menu with the AI option added
function SuggestionMenuWithAI({ editor }) {
  return (
    <SuggestionMenuController
      triggerCharacter="/"
      getItems={async (query) =>
        filterSuggestionItems(
          [
            ...getDefaultReactSlashMenuItems(editor),
            ...getAISlashMenuItems(editor),
          ],
          query
        )
      }
    />
  );
}

SuggestionMenuWithAI.propTypes = {
  editor: PropTypes.object.isRequired,
};

const TextEditor = ({ content, onChange, readOnly = false }) => {
  const { canvasState, updateContent } = useCanvas();

  const isUserEditingRef = useRef(false);
  const editorRef = useRef(null);
  const [initialBlocks, setInitialBlocks] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const lastProcessedContentRef = useRef(null);
  const editorReadyRef = useRef(false);
  const pendingBlocksRef = useRef(null);
  const previousStreamingStateRef = useRef(null);
  const isProgrammaticUpdateRef = useRef(false);
  const retryInProgressRef = useRef(false);
  const aiModel = useMemo(() => createAIModel(), []);

  // Check if we have meaningful content to display
  const hasContent = useMemo(() => {
    if (isLoadingContent) {
      return false;
    }

    // First check if we have actual content string
    const contentString = canvasState.content || content;
    if (typeof contentString === "string" && contentString.trim().length > 0) {
      return true;
    }

    // If we have blocks, check if any of them have meaningful content
    if (initialBlocks && initialBlocks.length > 0) {
      // Check if any block has text content
      const hasTextContent = initialBlocks.some((block) => {
        if (block.content && Array.isArray(block.content)) {
          return block.content.some(
            (item) =>
              item.type === "text" && item.text && item.text.trim().length > 0
          );
        }
        return false;
      });

      // Also check if we have non-paragraph blocks (like headers, lists, etc.)
      const hasNonEmptyBlocks = initialBlocks.some((block) => {
        // Consider headers, lists, and other block types as content
        if (block.type && block.type !== "paragraph") {
          return true;
        }
        // For paragraphs, check if they have content
        if (
          block.type === "paragraph" &&
          block.content &&
          Array.isArray(block.content)
        ) {
          return block.content.some(
            (item) =>
              item.type === "text" && item.text && item.text.trim().length > 0
          );
        }
        return false;
      });

      // Return true if we have text content OR non-empty blocks
      return hasTextContent || hasNonEmptyBlocks;
    }

    return false;
  }, [initialBlocks, isLoadingContent, canvasState.content, content]);

  // Debounce skeleton visibility to prevent flickering
  useEffect(() => {
    if (isLoadingContent || !hasContent) {
      const timer = setTimeout(() => {
        setShowSkeleton(true);
      }, 100); // Small delay to prevent rapid flickering

      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [isLoadingContent, hasContent]);

  // Add keyboard shortcut handlers for Cmd+A+I (AI) and Cmd+L (Link)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+L (or Ctrl+L on Windows/Linux) - Create Link
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "l" &&
        !event.shiftKey &&
        !event.altKey
      ) {
        event.preventDefault();

        if (editorRef.current) {
          try {
            const selectedText = editorRef.current.getSelectedText();
            const existingLink = editorRef.current.getSelectedLink();

            // Prompt for URL
            const url = prompt(
              existingLink
                ? `Edit link URL (current: ${existingLink}):`
                : "Enter URL:",
              existingLink || "https://"
            );

            if (url && url.trim()) {
              // If there's selected text, use it as the link text
              if (selectedText && selectedText.trim()) {
                editorRef.current.createLink(url.trim(), selectedText);
              } else {
                // If no text is selected, use the URL as both href and text
                editorRef.current.createLink(url.trim(), url.trim());
              }
            }
          } catch (error) {
            console.error("🎨 TextEditor: Error creating link:", error);
          }
        }
        return;
      }

      // Check for Cmd+A+I (or Ctrl+A+I on Windows/Linux) - AI Menu
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "i" &&
        !event.shiftKey &&
        event.altKey
      ) {
        event.preventDefault();

        if (aiModel && editorRef.current) {
          try {
            const aiExtension = getAIExtension(editorRef.current);
            const currentBlock =
              editorRef.current.getTextCursorPosition().block;
            aiExtension.openAIMenuAtBlock(currentBlock.id);
          } catch (error) {
            console.error("🎨 TextEditor: Error opening AI menu:", error);
          }
        }
        return;
      }
    };

    // Add event listener to document
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [aiModel]);

  // COMMENTED OUT: Initialize collaboration
  // const collaboration = useCollaboration({
  //   enabled: true, // Enable collaboration by default
  //   fragment: "document-store",
  // });

  // COMMENTED OUT: Debug collaboration status
  // useEffect(() => {
  //
  // }, [
  //   collaboration.roomId,
  //   collaboration.isCollaborationReady,
  //   collaboration.isConnected,
  //   collaboration.user,
  //   collaboration.connectedUsers.length,
  //   collaboration.connectionError,
  // ]);

  // Create the BlockNote editor with AI extension and collaboration
  const editor = useCreateBlockNote(
    {
      dictionary: {
        ...en,
        ai: aiEn, // add default translations for the AI extension
      },
      // Register the AI extension if model is available
      extensions: aiModel
        ? [
            createAIExtension({
              model: aiModel,
            }),
          ]
        : [],
      // COMMENTED OUT: Add collaboration configuration if ready
      // collaboration: collaboration.isCollaborationReady
      //   ? {
      //       provider: collaboration.provider,
      //       fragment: collaboration.fragment,
      //       user: collaboration.user,
      //       showCursorLabels: "activity", // Show cursor labels when users are active
      //     }
      //   : undefined,
      // Set initialContent if blocks are available, otherwise editor will start empty
      initialContent:
        initialBlocks && initialBlocks.length > 0 ? initialBlocks : undefined,
    },
    [
      // Dependencies for editor recreation
      // CRITICAL: Include initialBlocks.length to recreate editor when content first loads
      // This ensures the editor is recreated with initialContent when blocks become available
      // But doesn't recreate on every content change (only when going from 0 to N blocks)
      initialBlocks?.length > 0 ? "has-content" : "empty",
      aiModel,
      // COMMENTED OUT: collaboration dependencies
      // collaboration.isCollaborationReady,
      // collaboration.provider,
      // collaboration.fragment,
      // collaboration.user,
    ]
  );

  // Store editor reference and expose it globally for toolbar access
  useEffect(() => {
    editorRef.current = editor;

    // Expose editor to canvas context for undo/redo functionality
    if (window) {
      window.canvasBlockNoteEditor = editor;
    }

    // Mark editor as ready when it's available
    // Use a small delay to ensure editor is fully initialized
    const checkEditorReady = () => {
      // More robust check: editor exists, document is defined, and is an array
      const isReady =
        editor &&
        editor.document !== undefined &&
        Array.isArray(editor.document);

      if (isReady) {
        editorReadyRef.current = true;

        // If we have pending blocks, apply them now
        if (
          pendingBlocksRef.current &&
          Array.isArray(pendingBlocksRef.current) &&
          pendingBlocksRef.current.length > 0
        ) {
          try {
            // CRITICAL: Mark as programmatic update to prevent onChange from marking as dirty
            isProgrammaticUpdateRef.current = true;

            editor.replaceBlocks(editor.document, pendingBlocksRef.current);
            pendingBlocksRef.current = null;
            retryInProgressRef.current = false; // Clear retry flag since we successfully applied

            // Reset programmatic update flag after a delay
            setTimeout(() => {
              isProgrammaticUpdateRef.current = false;
            }, 150);
          } catch (error) {
            console.error(
              "🎨 TextEditor: Error applying pending blocks:",
              error,
              {
                hasDocument: !!editor.document,
                documentLength: editor.document?.length || 0,
                pendingBlocksLength: pendingBlocksRef.current?.length || 0,
              }
            );
            isProgrammaticUpdateRef.current = false; // Reset on error
            // Don't clear retryInProgressRef here - let the retry logic handle it
          }
        }
      } else {
        editorReadyRef.current = false;
      }
    };

    // Check immediately and after multiple delays to catch editor initialization
    checkEditorReady();
    const timeoutId1 = setTimeout(checkEditorReady, 50);
    const timeoutId2 = setTimeout(checkEditorReady, 150);
    const timeoutId3 = setTimeout(checkEditorReady, 300);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [editor]);

  // Convert markdown content to blocks when content changes
  useEffect(() => {
    // Reset editor readiness when sourceMessageId changes (new canvas)
    const currentSourceMessageId = canvasState.sourceMessageId;
    if (
      currentSourceMessageId !==
      lastProcessedContentRef.current?.sourceMessageId
    ) {
      editorReadyRef.current = false;
      pendingBlocksRef.current = null;
    }

    const convertContentToBlocks = async () => {
      if (!editor) {
        return;
      }

      // Check if we already have the same content to avoid unnecessary conversions
      const currentContent = canvasState.content || content;

      // CRITICAL FIX: Reset lastProcessedContentRef when sourceMessageId changes
      // This ensures content updates when switching between different canvases
      const lastProcessedContentRefValue = lastProcessedContentRef.current;
      const lastSourceMessageId = lastProcessedContentRefValue?.sourceMessageId;

      // On first load, lastProcessedContentRef.current will be null
      // Also check if sourceMessageId changed (including null -> value)
      const sourceMessageIdChanged =
        lastSourceMessageId !== currentSourceMessageId;

      // Check if streaming is complete
      const isStreamingComplete = canvasState.isStreaming === false;
      const wasStreaming = previousStreamingStateRef.current === true;
      const streamingJustCompleted = wasStreaming && isStreamingComplete;

      // Update the previous streaming state
      previousStreamingStateRef.current = canvasState.isStreaming;

      // If sourceMessageId changed or we don't have a stored value, we need to process
      if (sourceMessageIdChanged || !lastProcessedContentRefValue) {
        // Continue processing - don't skip
      } else if (streamingJustCompleted) {
        // Streaming just completed - ALWAYS process to ensure final content is shown
        // Continue processing - don't skip
      } else if (
        lastProcessedContentRefValue?.content === currentContent &&
        lastProcessedContentRefValue?.sourceMessageId === currentSourceMessageId
      ) {
        // Content and sourceMessageId are the same, skip conversion
        return;
      }

      // Store the content we're about to process BEFORE processing
      // This prevents multiple conversions of the same content
      lastProcessedContentRef.current = {
        content: currentContent,
        sourceMessageId: currentSourceMessageId,
      };

      setIsLoadingContent(true);

      try {
        let markdownContent = "";

        // Get markdown content from canvas state or prop
        if (canvasState.content && typeof canvasState.content === "string") {
          markdownContent = cleanMarkdownContent(canvasState.content);
        } else if (content && typeof content === "string") {
          markdownContent = cleanMarkdownContent(content);
        } else if (Array.isArray(content)) {
          // If content is already blocks, use them directly
          const finalBlocks =
            content.length > 0 ? content : [createDefaultBlock()];
          setInitialBlocks(finalBlocks);

          // Store initial blocks globally for custom history plugin
          if (window) {
            window.canvasInitialBlocks = finalBlocks;
          }

          setIsLoadingContent(false);
          return;
        }

        // Process markdown content (even if empty, we'll create a default block)
        if (markdownContent.trim()) {
          // Use BlockNote's built-in markdown conversion
          const blocks = await editor.tryParseMarkdownToBlocks(markdownContent);

          // Ensure we always have at least one block
          const finalBlocks =
            blocks.length > 0 ? blocks : [createDefaultBlock()];
          setInitialBlocks(finalBlocks);

          // Store initial blocks globally for custom history plugin
          if (window) {
            window.canvasInitialBlocks = finalBlocks;
          }
        } else {
          // Empty content - use default empty block
          const defaultBlocks = [createDefaultBlock()];
          setInitialBlocks(defaultBlocks);

          // Store initial blocks globally for custom history plugin
          if (window) {
            window.canvasInitialBlocks = defaultBlocks;
          }
        }
      } catch (error) {
        console.error("🎨 TextEditor: Error during content conversion:", error);
        // Fallback to default block on error
        const fallbackBlocks = [createDefaultBlock()];
        setInitialBlocks(fallbackBlocks);

        // Store initial blocks globally for custom history plugin
        if (window) {
          window.canvasInitialBlocks = fallbackBlocks;
        }
      } finally {
        setIsLoadingContent(false);
      }
    };

    convertContentToBlocks();
  }, [
    editor,
    canvasState.content,
    content,
    canvasState.sourceMessageId,
    canvasState.isStreaming,
    canvasState.isOpen,
  ]);

  // Update editor content when blocks change
  useEffect(() => {
    if (!editor || isLoadingContent || !initialBlocks) {
      return;
    }

    // Don't update if the source was user input
    const lastUpdateSource = canvasState.metadata?.lastUpdateSource;
    if (lastUpdateSource === "user") {
      return;
    }

    // Don't update if user is currently editing
    if (isUserEditingRef.current) {
      return;
    }

    // Ensure we have blocks before replacing
    if (initialBlocks.length > 0) {
      const applyBlocks = () => {
        try {
          // Check if editor document exists
          if (!editor.document) {
            console.warn("🎨 TextEditor: Editor document not available yet");
            return false;
          }

          // CRITICAL: Mark as programmatic update to prevent onChange from marking as dirty
          isProgrammaticUpdateRef.current = true;

          // Replace blocks
          editor.replaceBlocks(editor.document, initialBlocks);

          pendingBlocksRef.current = null; // Clear pending blocks
          retryInProgressRef.current = false; // Mark retry as complete if it was in progress

          // Reset programmatic update flag after a delay to allow onChange to process
          setTimeout(() => {
            isProgrammaticUpdateRef.current = false;
          }, 150);

          return true;
        } catch (error) {
          console.error("🎨 TextEditor: Error updating editor content:", error);
          isProgrammaticUpdateRef.current = false; // Reset on error
          return false;
        }
      };

      // Try to apply blocks immediately if editor is ready
      if (editorReadyRef.current && editor.document) {
        if (!applyBlocks()) {
          console.warn(
            "🎨 TextEditor: Immediate application failed, storing as pending"
          );
          // If immediate application failed, store as pending only if not already retrying
          if (!retryInProgressRef.current) {
            pendingBlocksRef.current = initialBlocks;
          }
        }
      } else {
        // Editor not ready yet - only set pending blocks if we don't have any or retry isn't in progress
        // This prevents clearing pending blocks during streaming updates
        if (!retryInProgressRef.current && !pendingBlocksRef.current) {
          pendingBlocksRef.current = initialBlocks;

          // Retry with exponential backoff using refs to avoid closure issues
          const retryCountRef = { current: 0 };
          const maxRetries = 10; // Increased retries for slower initialization
          const retryDelays = [
            50, 100, 150, 200, 250, 300, 400, 500, 750, 1000,
          ];

          const attemptApply = () => {
            // Check if we still have pending blocks - if not, stop retrying
            const currentPendingBlocks = pendingBlocksRef.current;
            if (
              !currentPendingBlocks ||
              !Array.isArray(currentPendingBlocks) ||
              currentPendingBlocks.length === 0
            ) {
              retryInProgressRef.current = false;
              return;
            }

            if (retryCountRef.current >= maxRetries) {
              console.error("🎨 TextEditor: Max retries reached, giving up", {
                editorReady: editorReadyRef.current,
                hasEditor: !!editorRef.current,
                hasDocument: !!editorRef.current?.document,
                hasPendingBlocks: !!pendingBlocksRef.current,
                pendingBlocksLength: pendingBlocksRef.current?.length || 0,
              });
              // Clear pending blocks to prevent memory leaks
              pendingBlocksRef.current = null;
              retryInProgressRef.current = false;
              return;
            }

            // Capture current editor and pending blocks at retry time
            const currentEditor = editorRef.current;

            // More robust check for editor readiness
            const isEditorReady =
              currentEditor &&
              currentEditor.document !== undefined &&
              Array.isArray(currentEditor.document) &&
              currentEditor.document.length >= 0; // Allow empty document

            if (
              isEditorReady &&
              currentPendingBlocks &&
              Array.isArray(currentPendingBlocks)
            ) {
              try {
                // CRITICAL: Mark as programmatic update to prevent onChange from marking as dirty
                isProgrammaticUpdateRef.current = true;

                currentEditor.replaceBlocks(
                  currentEditor.document,
                  currentPendingBlocks
                );
                pendingBlocksRef.current = null; // Clear pending blocks on success
                retryInProgressRef.current = false; // Mark retry as complete

                // Reset programmatic update flag after a delay
                setTimeout(() => {
                  isProgrammaticUpdateRef.current = false;
                }, 150);

                return; // Success, stop retrying
              } catch (error) {
                console.error("🎨 TextEditor: Error in retry attempt:", error, {
                  retryCount: retryCountRef.current,
                  hasDocument: !!currentEditor?.document,
                  documentLength: currentEditor?.document?.length || 0,
                  pendingBlocksLength: currentPendingBlocks?.length || 0,
                });
                isProgrammaticUpdateRef.current = false; // Reset on error
              }
            } else {
              // Log why we're retrying for debugging
              // Previously logged retry reasons here, but removed to reduce console noise
            }

            // Schedule next retry
            retryCountRef.current++;
            const delay =
              retryDelays[
                Math.min(retryCountRef.current - 1, retryDelays.length - 1)
              ];
            setTimeout(attemptApply, delay);
          };

          // Mark that retry is in progress
          retryInProgressRef.current = true;

          // Start retry sequence after initial delay
          setTimeout(attemptApply, 50);
        } else if (retryInProgressRef.current) {
          // If retry is in progress, update the pending blocks to the latest content
          // This ensures we apply the most recent content when the editor becomes ready
          pendingBlocksRef.current = initialBlocks;
        }
      }
    }
  }, [
    editor,
    initialBlocks,
    isLoadingContent,
    canvasState.metadata?.lastUpdateSource,
  ]);

  // Handle content changes from user editing
  const handleChange = useCallback(async () => {
    if (readOnly || !editor) return;

    // CRITICAL: Skip if this is a programmatic update (not a user edit)
    // This prevents marking the canvas as dirty when loading content
    if (isProgrammaticUpdateRef.current) {
      return;
    }

    // Mark that user is editing to prevent external updates
    isUserEditingRef.current = true;

    try {
      const blocks = editor.document;

      // Convert blocks back to markdown using BlockNote's built-in method
      const markdown = await editor.blocksToMarkdownLossy(blocks);

      // Update canvas context with markdown content
      if (updateContent) {
        updateContent(markdown, "user");
      }

      // Call external onChange if provided
      if (onChange) {
        onChange(markdown);
      }
    } catch (error) {
      console.error(
        "🎨 TextEditor: Error converting blocks to markdown:",
        error
      );
    } finally {
      // Reset editing flag after a short delay to allow for external updates
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
    }
  }, [editor, onChange, updateContent, readOnly]);

  // Show skeleton if loading or no meaningful content
  if (showSkeleton) {
    const isDark = document.documentElement.classList.contains("dark");

    return (
      <div
        className="h-full w-full p-4 space-y-4"
        style={{ backgroundColor: isDark ? "#2F2F2F" : "#FAFAFA" }}
      >
        {/* Multiple skeleton lines to simulate text content */}
        <Skeleton
          className={`h-6 w-3/4 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
        />
        <Skeleton
          className={`h-6 w-full ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
        />
        <Skeleton
          className={`h-6 w-5/6 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
        />
        <div className="space-y-2 mt-8">
          <Skeleton
            className={`h-6 w-2/3 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-6 w-4/5 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-6 w-3/4 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>
        <div className="space-y-2 mt-8">
          <Skeleton
            className={`h-6 w-1/2 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-6 w-full ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-6 w-2/3 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>
        <div className="space-y-2 mt-8">
          <Skeleton
            className={`h-6 w-4/5 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-6 w-3/5 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>
      </div>
    );
  }

  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div
      className="h-full w-full overflow-auto relative"
      data-testid={TEST_IDS.CANVAS_TEXT_EDITOR}
      style={{
        backgroundColor: isDark ? "#2F2F2F" : "#FAFAFA",
        "--bn-colors-editor-background": isDark ? "#2F2F2F" : "#FAFAFA",
        "--bn-colors-editor-text": isDark ? "#FFFFFF" : "#1a1a1a",
        "--bn-colors-menu-background": isDark ? "#3F3F3F" : "#FFFFFF",
        "--bn-colors-menu-text": isDark ? "#FFFFFF" : "#1a1a1a",
        "--bn-colors-tooltip-background": isDark ? "#3F3F3F" : "#333333",
        "--bn-colors-tooltip-text": isDark ? "#FFFFFF" : "#FFFFFF",
        "--bn-colors-hovered-background": isDark ? "#4F4F4F" : "#F0F0F0",
        "--bn-colors-selected-background": isDark ? "#4F4F4F" : "#E0E0E0",
        "--bn-colors-disabled-background": isDark ? "#2F2F2F" : "#FAFAFA",
        "--bn-colors-disabled-text": isDark ? "#888888" : "#999999",
        "--bn-colors-shadow": isDark
          ? "rgba(0, 0, 0, 0.3)"
          : "rgba(0, 0, 0, 0.1)",
        "--bn-colors-border": isDark ? "#555555" : "#E0E0E0",
        "--bn-colors-side-menu": isDark ? "#3F3F3F" : "#F5F5F5",
        "--bn-colors-highlights-gray-background": isDark
          ? "#4F4F4F"
          : "#E8E8E8",
        "--bn-colors-highlights-gray-text": isDark ? "#FFFFFF" : "#1a1a1a",
      }}
    >
      {/* COMMENTED OUT: Collaboration Status Bar */}
      {/* {collaboration.roomId && (
        <div className="absolute top-4 right-4 z-10">
          <CollaborationStatus
            isConnected={collaboration.isConnected}
            connectedUsers={collaboration.connectedUsers}
            connectionError={collaboration.connectionError}
            roomId={collaboration.roomId}
            user={collaboration.user}
          />
        </div>
      )} */}
      <style>
        {`
          .bn-editor {
            background-color: ${isDark ? "#2F2F2F" : "#FAFAFA"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            min-height: 100vh !important;
          }
          .bn-editor .ProseMirror {
            background-color: ${isDark ? "#2F2F2F" : "#FAFAFA"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            padding: 16px !important;
            min-height: calc(100vh - 64px) !important;
          }
          .bn-editor .ProseMirror p {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-editor .ProseMirror h1,
          .bn-editor .ProseMirror h2,
          .bn-editor .ProseMirror h3 {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-editor .ProseMirror li {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-editor .ProseMirror pre {
            background-color: ${isDark ? "#1F1F1F" : "#F5F5F5"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            border: 1px solid ${isDark ? "#555555" : "#E0E0E0"} !important;
          }
          .bn-editor .ProseMirror code {
            background-color: ${isDark ? "#1F1F1F" : "#F5F5F5"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-editor .ProseMirror blockquote {
            border-left: 4px solid ${isDark ? "#555555" : "#D0D0D0"} !important;
            background-color: ${isDark ? "#3F3F3F" : "#F5F5F5"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-side-menu {
            background-color: ${isDark ? "#3F3F3F" : "#F5F5F5"} !important;
          }
          .bn-drag-handle {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
          }
          .bn-drag-handle:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-add-block-button {
            background-color: transparent !important;
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
            border: 1px solid ${isDark ? "#555555" : "#D0D0D0"} !important;
          }
          .bn-add-block-button:hover {
            background-color: transparent !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            border-color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-side-menu button {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
            background-color: transparent !important;
          }
          .bn-side-menu button:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-side-menu svg {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
          }
          .bn-side-menu svg:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          /* Format toolbar buttons */
          .bn-toolbar button {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
            background-color: transparent !important;
          }
          .bn-toolbar button:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-toolbar svg {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
          }
          .bn-toolbar svg:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          /* Menu items */
          .bn-menu-item {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-menu-item:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-menu-item svg {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
          }
          /* All buttons and icons in BlockNote */
          .bn-editor button {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
            background-color: transparent !important;
          }
          .bn-editor button:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-editor svg {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
          }
          .bn-editor svg:hover {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          /* AI Menu specific styling */
          .bn-ai-menu {
            background-color: ${isDark ? "#3F3F3F" : "#FFFFFF"} !important;
            border: 1px solid ${isDark ? "#555555" : "#E0E0E0"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-ai-menu-item {
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
            background-color: transparent !important;
          }
          .bn-ai-menu-item:hover {
            background-color: ${isDark ? "#4F4F4F" : "#F0F0F0"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          .bn-ai-toolbar-button {
            color: ${isDark ? "#FFFFFF" : "#666666"} !important;
            background-color: transparent !important;
          }
          .bn-ai-toolbar-button:hover {
            background-color: ${isDark ? "#4F4F4F" : "#F0F0F0"} !important;
            color: ${isDark ? "#FFFFFF" : "#1a1a1a"} !important;
          }
          /* Override link button tooltip to show Cmd+L instead of Cmd+K */
          .bn-toolbar button[aria-label*="Create link"] {
            position: relative;
          }
          .bn-toolbar button[aria-label*="Create link"]:hover::after {
            content: "Create link ⌘L" !important;
            position: absolute;
            bottom: -30px;
            left: 50%;
            transform: translateX(-50%);
            background-color: #3F3F3F;
            color: #FFFFFF;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 1000;
            pointer-events: none;
          }
          /* Hide the default tooltip */
          .bn-toolbar button[aria-label*="Create link"]:hover::before {
            display: none !important;
          }
        `}
      </style>
      <BlockNoteView
        editor={editor}
        onChange={handleChange}
        editable={!readOnly}
        className="min-h-full"
        theme="dark"
        formattingToolbar={false}
        slashMenu={false}
      >
        {/* AI Menu Controller - only render if AI model is available */}
        {aiModel && <AIMenuController aiMenu={CustomAIMenu} />}

        {/* Custom Formatting Toolbar with AI Button */}
        <FormattingToolbarWithAI />

        {/* Custom Slash Menu with AI Options */}
        <SuggestionMenuWithAI editor={editor} />
      </BlockNoteView>
    </div>
  );
};

TextEditor.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default TextEditor;
